import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import EditLayout from './EditLayout';
import EditProfileLayout from './EditProfileLayout';
import { theme } from '../../theme/theme.js';
import AppToast from '../AppToast';
import './css/EditLayout.css';
import './css/EditLayoutModal.css';

const EditLayoutModal = ({ show, onHide, objectName, id, isClone = false, onSave, onCancel, sourcePage, queryParams, onRefresh, useProfileLayout = false }) => {
  const [formRef, setFormRef] = React.useState(null);
  const [isSaveAndNew, setIsSaveAndNew] = React.useState(false);
  const [toast, setToast] = React.useState({
    show: false,
    message: '',
    variant: 'success'
  });

  const handleClose = () => {
    onHide();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onHide();
    }
  };

  const handleSave = (savedData) => {
    // Determine if this is a new record or update
    const isNewRecord = !id || isClone;
    const successMessage = isNewRecord
      ? (isClone ? 'Record cloned successfully!' : 'Record created successfully!')
      : 'Record updated successfully!';

    // Show toast notification first
    setToast({
      show: true,
      message: successMessage,
      variant: 'success'
    });

    // Auto-hide toast after 3 seconds
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 3000);

    if (onSave) {
      onSave(savedData);
    }
    console.log("Handle Save: ", isSaveAndNew);

    // Since EditLayoutModal is always opened in a modal context,
    // we should NOT redirect. Just close the modal and let the onSave callback
    // handle any refresh logic. This matches the behavior of ResizableTable
    // and RelatedTabsModal where records are created/edited without redirecting.

    // Close the modal - the parent component (via onSave callback) will handle refresh
    // Use a small delay to ensure toast is rendered before modal closes
    setTimeout(() => {
      onHide();
    }, 100);

    // Optionally trigger a refresh message for iframe contexts
    const recordId = savedData?.id || savedData?.data?.id || id;
    if (recordId && window.parent && window.parent !== window) {
      window.parent.postMessage({
        type: 'recordSaved',
        action: 'refresh',
        objectName: objectName,
        recordId: recordId
      }, '*');
    }
  };

  const handleSaveAndNew = (savedData) => {
    console.log("$$ handleSaveAndNew");

    // Determine if this is a new record or update
    const isNewRecord = !id || isClone;
    const successMessage = isNewRecord
      ? (isClone ? 'Record cloned successfully!' : 'Record created successfully!')
      : 'Record updated successfully!';

    // Show toast notification
    setToast({
      show: true,
      message: successMessage,
      variant: 'success'
    });

    // Auto-hide toast after 3 seconds
    setTimeout(() => {
      setToast(prev => ({ ...prev, show: false }));
    }, 3000);

    if (onRefresh) {
      console.log("$$ onRefresh for relatedlist");
      onRefresh();
      return;
    }

    const refreshEvent = new CustomEvent('listViewRefresh', {
      detail: {
        objectName: objectName,
        recordId: savedData?.id || savedData?.data?.id || id,
        action: 'recordSavedAndNew'
      }
    });
    window.dispatchEvent(refreshEvent);


  };

  const triggerSave = () => {
    if (formRef) {
      formRef.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  };

  const triggerSaveAndNew = async () => {
    console.log("triggerSaveAndNew");
    setIsSaveAndNew(true);
    if (formRef) {
      // Create a custom event with saveAndNew flag
      const saveAndNewEvent = new CustomEvent('submit', {
        bubbles: true,
        cancelable: true,
        detail: { saveAndNew: true }
      });
      formRef.dispatchEvent(saveAndNewEvent);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="xl"
        centered
        className="edit-layout-modal"
        backdrop={false}
      >
        <Modal.Header className="modal-theme-header">
          <Modal.Title style={{ color: theme.colors.white }}>
            <div className="title-content">
              <div className="title-icon" style={{
                width: '40px',
                height: '40px',
                background: theme.colors.white,
                borderRadius: theme.borderRadius.lg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.colors.primary,
                fontSize: '16px'
              }}>
                <i className="fas fa-plus-circle"></i>
              </div>
              <div className="title-text">
                <div className="title-subtitle" style={{
                  fontSize: theme.typography.fontSize.xs,
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontWeight: theme.typography.fontWeight.medium,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  marginBottom: '2px'
                }}>{useProfileLayout ? 'PROFILE' : (isClone ? 'CLONE' : (id ? 'EDIT' : 'NEW'))}</div>
                <h2 className="title-main" style={{
                  fontSize: theme.typography.fontSize.lg,
                  fontWeight: theme.typography.fontWeight.semibold,
                  color: theme.colors.white,
                  margin: 0,
                  lineHeight: 1.2
                }}>{useProfileLayout ? 'Edit Profile' : objectName.toUpperCase()}</h2>
              </div>
            </div>
          </Modal.Title>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close"
            className="btn-close"
          >
            <i className="fas fa-times"></i>
          </button>
        </Modal.Header>

        <Modal.Body className="modal-theme-body p-0">
          {useProfileLayout ? (
            <EditProfileLayout
              objectName={objectName}
              id={isClone ? null : id}
              onSave={handleSave}
              onCancel={handleCancel}
              hideHeader={true}
              formRef={setFormRef}
              openedInModal={true}
            />
          ) : (
            <EditLayout
              objectName={objectName}
              id={isClone ? null : id}
              cloneFromId={isClone ? id : null}
              onSave={handleSave}
              onSaveAndNew={handleSaveAndNew}
              onCancel={handleCancel}
              sourcePage={sourcePage}
              hideHeader={true}
              formRef={setFormRef}
              queryParams={queryParams}
              openedInModal={true}
            />
          )}
        </Modal.Body>

        <Modal.Footer className="modal-theme-footer">
          <div className="footer-actions">
            <Button
              variant="outline-secondary"
              onClick={handleClose}
              className="cancel-btn"
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                border: `1px solid ${theme.colors.borderMedium}`,
                background: theme.colors.white,
                color: theme.colors.textSecondary,
                borderRadius: theme.borderRadius.sm,
                transition: theme.transitions.normal,
                minWidth: '80px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = theme.colors.light;
                e.target.style.borderColor = theme.colors.borderDark;
                e.target.style.color = theme.colors.textPrimary;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = theme.colors.white;
                e.target.style.borderColor = theme.colors.borderMedium;
                e.target.style.color = theme.colors.textSecondary;
              }}
            >
              Cancel
            </Button>
            {(!id || isClone) && !useProfileLayout && (
              <Button
                variant="outline-primary"
                onClick={() => {
                  // Trigger save and prepare for new record
                  triggerSaveAndNew();
                }}
                className="save-new-btn"
                style={{
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: theme.typography.fontWeight.medium,
                  border: `1px solid ${theme.colors.secondary}`,
                  background: theme.colors.white,
                  color: theme.colors.secondary,
                  borderRadius: theme.borderRadius.sm,
                  transition: theme.transitions.normal,
                  minWidth: '100px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = theme.colors.secondary;
                  e.target.style.color = theme.colors.white;
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = theme.colors.white;
                  e.target.style.color = theme.colors.secondary;
                }}
              >
                Save & New
              </Button>
            )}
            <Button
              variant="primary"
              onClick={() => {
                // Trigger save
                triggerSave();
              }}
              className="save-btn"
              style={{
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                fontSize: theme.typography.fontSize.sm,
                fontWeight: theme.typography.fontWeight.medium,
                background: theme.colors.secondary,
                border: `1px solid ${theme.colors.secondary}`,
                color: theme.colors.white,
                borderRadius: theme.borderRadius.sm,
                transition: theme.transitions.normal,
                minWidth: '80px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = theme.colors.secondaryDark;
                e.target.style.borderColor = theme.colors.secondaryDark;
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = `0 2px 4px rgba(0, 123, 255, 0.2)`;
              }}
              onMouseLeave={(e) => {
                e.target.style.background = theme.colors.secondary;
                e.target.style.borderColor = theme.colors.secondary;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              Save
            </Button>

          </div>
        </Modal.Footer>
      </Modal>

      {/* AppToast for success messages - rendered outside Modal so it persists after modal closes */}
      <AppToast
        show={toast.show}
        message={toast.message}
        variant={toast.variant}
        onClose={() => setToast(prev => ({ ...prev, show: false }))}
        delay={3000}
      />
    </>
  );
};

export default EditLayoutModal;
